import { Controller } from "@hotwired/stimulus";

// Drives the "Refund Payment" modal. A single modal instance is shared across all
// payment rows; the triggering dropdown button supplies the payment context (slug +
// amounts) via data attributes, which we read from the Bootstrap `show.bs.modal`
// event's relatedTarget.
export default class extends Controller {
  static targets = [
    "form", "submitBtn", "paymentSlug",
    "fullRadio", "partialRadio", "itemsRadio",
    "fullDescription", "partialDescription",
    "amountContainer", "amountInput",
    "itemsContainer", "itemCheckbox", "itemsTotal",
    "refundedSoFarContainer", "refundedSoFar"
  ];

  static values = { baseUrl: String };

  connect() {
    this._boundShow = this.onShow.bind(this);
    this.element.addEventListener("show.bs.modal", this._boundShow);
    this.currencyFormatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
    this.refundableAmount = 0;
  }

  disconnect() {
    this.element.removeEventListener("show.bs.modal", this._boundShow);
  }

  onShow(event) {
    const trigger = event.relatedTarget;
    if (!trigger) return;

    const slug = trigger.dataset.paymentSlug;
    const totalAmount = parseFloat(trigger.dataset.paymentTotalAmount || "0");
    const refundedAmount = parseFloat(trigger.dataset.paymentRefundedAmount || "0");
    this.refundableAmount = parseFloat(trigger.dataset.paymentRefundableAmount || "0");

    this.formTarget.setAttribute("action", `${this.baseUrlValue}/${slug}/refund`);
    this.paymentSlugTarget.textContent = slug;
    this.fullDescriptionTarget.textContent =
      `Refund the remaining ${this._format(this.refundableAmount)}.`;
    this.partialDescriptionTarget.textContent =
      `Choose an amount up to ${this._format(this.refundableAmount)} to refund.`;

    if (refundedAmount > 0) {
      this.refundedSoFarTarget.textContent =
        `${this._format(refundedAmount)} of ${this._format(totalAmount)}`;
      this.refundedSoFarContainerTarget.classList.remove("d-none");
    } else {
      this.refundedSoFarContainerTarget.classList.add("d-none");
    }

    this._reset();
  }

  typeChanged() {
    this._syncTypeState();
  }

  amountChanged() {
    this._updateSubmitState();
  }

  itemsChanged() {
    if (this.hasItemsTotalTarget) {
      this.itemsTotalTarget.textContent = this._format(this._selectedItemsTotal());
    }
    this._updateSubmitState();
  }

  _reset() {
    this.fullRadioTarget.checked = true;
    this.amountInputTarget.value = "";
    this.amountInputTarget.max = this.refundableAmount;
    this.itemCheckboxTargets.forEach((checkbox) => { checkbox.checked = false; });
    this._resetReasonSelect();
    this._syncTypeState();
    this.itemsChanged();
  }

  _syncTypeState() {
    this.amountContainerTarget.classList.toggle("d-none", !this.partialRadioTarget.checked);
    if (this.hasItemsContainerTarget) {
      this.itemsContainerTarget.classList.toggle("d-none", !this.itemsRadioTarget.checked);
    }
    this._updateSubmitState();
  }

  _updateSubmitState() {
    this.submitBtnTarget.disabled = !this._validRefundRequest();
  }

  _validRefundRequest() {
    if (this.hasItemsRadioTarget && this.itemsRadioTarget.checked) {
      return this._selectedItemsTotal() > 0;
    }
    if (this.fullRadioTarget.checked) return true;

    const amount = parseFloat(this.amountInputTarget.value);
    return !isNaN(amount) && amount > 0 && amount <= this.refundableAmount;
  }

  _selectedItemsTotal() {
    return this.itemCheckboxTargets
      .filter((checkbox) => checkbox.checked)
      .reduce((total, checkbox) => total + parseFloat(checkbox.dataset.refundAmount || "0"), 0);
  }

  _format(value) {
    return this.currencyFormatter.format(value);
  }

  _resetReasonSelect() {
    const input = this._reasonInput;
    if (!input) return;
    input.value = "";
    const wrapper = input.closest(".app-select");
    if (wrapper) {
      const label = wrapper.querySelector('[data-app-select-target="label"]');
      if (label) label.textContent = "Select a reason";
      wrapper.querySelectorAll(".app-select-option").forEach((el) => {
        el.classList.remove("app-select-option-selected");
      });
    }
  }

  get _reasonInput() {
    return this.element.querySelector('input[name="reason"]');
  }
}
